import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Loader2, ShieldCheck } from "lucide-react";
import LuvmeIcon from "@/components/LuvmeIcon";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [twofa, setTwofa] = useState(null); // { user_id, email, demo_code }
  const [code, setCode] = useState("");

  const goByRole = (user) =>
    navigate(user.role === "admin" ? "/admin" : user.role === "creator" ? "/dashboard" : "/feed");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.twofa_required) {
        setTwofa(res);
      } else {
        goByRole(res);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await verify2fa(twofa.user_id, code.trim());
      goByRole(user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0D0D0D]">
      <div className="hidden lg:block relative">
        <img src={IMAGES.auth} alt="Luvme" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/40 to-transparent" />
        <Link to="/" className="absolute top-8 left-8 flex items-center gap-2 z-10">
          <LuvmeIcon className="w-6 h-6 sm:w-7 sm:h-7" />
          <span className="text-2xl font-black font-heading">Luvme</span>
        </Link>
        <div className="absolute bottom-12 left-8 right-8">
          <h2 className="text-4xl font-black font-heading leading-tight">Il tuo talento, i tuoi guadagni.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-10">
            <Sparkles className="w-6 h-6 text-[#E91E8C]" />
            <span className="text-2xl font-black font-heading">Luvme</span>
          </Link>
          <h1 className="text-3xl font-black font-heading mb-2">Accedi al tuo account</h1>
          <p className="text-white/60 mb-8">Bentornato su Luvme.</p>

          {twofa ? (
            <form onSubmit={submitCode} className="space-y-5" data-testid="twofa-form">
              <div className="flex items-center gap-3 rounded-xl border border-[#E91E8C]/30 bg-[#E91E8C]/10 p-3">
                <ShieldCheck className="w-5 h-5 text-[#E91E8C] shrink-0" />
                <p className="text-sm text-white/80">
                  Abbiamo inviato un codice a 6 cifre a <span className="font-semibold">{twofa.email}</span>.
                </p>
              </div>
              {twofa.demo_code && (
                <p className="text-xs text-amber-300/90 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2" data-testid="twofa-demo-code">
                  Modalità demo: il tuo codice è <span className="font-bold tracking-widest">{twofa.demo_code}</span>
                </p>
              )}
              <div>
                <Label htmlFor="otp" className="text-white/80">Codice di verifica</Label>
                <Input
                  id="otp" inputMode="numeric" required value={code} data-testid="twofa-code"
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="mt-2 bg-[#1A1A1A] border-white/10 h-12 rounded-xl text-center text-2xl tracking-[0.5em] focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                  placeholder="000000"
                />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="twofa-error">{error}</p>}
              <Button
                type="submit" disabled={loading || code.length < 6} data-testid="twofa-submit"
                className="w-full h-12 rounded-xl bg-[#E91E8C] hover:bg-[#D8157B] text-white font-semibold hover:shadow-[0_0_18px_rgba(233,30,140,0.5)] transition-all disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verifica e accedi"}
              </Button>
              <button type="button" onClick={() => { setTwofa(null); setCode(""); setError(""); }} data-testid="twofa-back"
                className="w-full text-sm text-white/50 hover:text-white transition-colors">
                Torna indietro
              </button>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-white/80">Email</Label>
              <Input
                id="email" type="email" required value={email} data-testid="login-email"
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-[#1A1A1A] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                placeholder="tu@esempio.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-white/80">Password</Label>
                <Link to="/recupero-password" className="text-xs text-[#E91E8C] hover:underline" data-testid="forgot-password-link">
                  Password dimenticata?
                </Link>
              </div>
              <Input
                id="password" type="password" required value={password} data-testid="login-password"
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-[#1A1A1A] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                placeholder="••••••••"
              />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
            <Button
              type="submit" disabled={loading} data-testid="login-submit"
              className="w-full h-12 rounded-xl bg-[#E91E8C] hover:bg-[#D8157B] text-white font-semibold hover:shadow-[0_0_18px_rgba(233,30,140,0.5)] transition-all"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Accedi"}
            </Button>
          </form>
          )}

          <p className="text-center text-white/60 mt-8 text-sm">
            Non hai un account?{" "}
            <Link to="/register" className="text-[#E91E8C] font-semibold hover:underline" data-testid="go-register">Registrati</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
